import { Link } from "react-router-dom";
import {
  ArrowRight,
  CalendarDays,
  CalendarRange,
  CheckCircle2,
  Clock,
  LayoutGrid,
  Sparkles,
} from "lucide-react";
import "../../../styles/rosterPlannerGuide.css";

const SETUP_STEPS = [
  {
    title: "Define shifts first",
    body: "Create shift timings, grace, and night rules under Settings → Shift Management, then assign default shifts to employees in Attendance → Shift Management.",
  },
  {
    title: "Maintain holidays separately",
    body: "Add organisation holidays in Holiday Calendar. Rosters use holidays for planning context — they are not the same as a monthly roster plan.",
  },
  {
    title: "Create a monthly roster plan",
    body: "Open the roster planner, click New roster, and set the plan name and period (typically one calendar month).",
  },
  {
    title: "Assign shifts to employees",
    body: "Use quick assignment, the calendar grid, bulk copy, or CSV import to map each employee to a shift for each work date.",
  },
  {
    title: "Validate and submit",
    body: "Review validation messages (overlaps, missing shifts, etc.), then submit the plan for approval when draft work is complete.",
  },
  {
    title: "Approve, publish, freeze",
    body: "After approval, publish so attendance can use the roster. Freeze when the month is locked; archive when you no longer need edits.",
  },
];

const LIFECYCLE = [
  { label: "Draft", active: true },
  { label: "Pending approval", active: false },
  { label: "Approved", active: false },
  { label: "Published", active: false },
  { label: "Frozen", active: false },
];

export default function RosterPlannerGuidePage() {
  return (
    <div className="rpg-shell">
      <div className="rpg-inner">
        <section className="rpg-hero" aria-labelledby="rpg-title">
          <div className="rpg-hero-glow rpg-hero-glow--a" aria-hidden />
          <div className="rpg-hero-glow rpg-hero-glow--b" aria-hidden />

          <div className="rpg-hero-grid">
            <div>
              <div className="rpg-hero-icon" aria-hidden>
                <LayoutGrid size={26} strokeWidth={2} />
              </div>
              <h1 id="rpg-title">Roster planner setup guide</h1>
              <p>
                Plan who works which shift, day by day. This guide walks you through prerequisites,
                assignments, and the approval lifecycle before you open the live roster board.
              </p>
            </div>

            <div className="text-left md:text-right">
              <Link to="/attendance/roster" className="rpg-cta-primary">
                Open roster planner
                <ArrowRight size={18} />
              </Link>
              <Link to="/settings/attendance-leave-setup?tab=shifts" className="rpg-cta-secondary">
                Configure shifts in settings
              </Link>
            </div>
          </div>

          <div className="rpg-split">
            <div className="rpg-note">
              <strong>
                <Sparkles size={14} className="inline mr-1 -mt-0.5" />
                Roster vs holiday calendar
              </strong>
              Rosters schedule employee shifts by date. Holiday Calendar lists organisation-wide
              non-working days. Configure both — they serve different purposes.
            </div>
            <div className="rpg-note">
              <strong>
                <CheckCircle2 size={14} className="inline mr-1 -mt-0.5" />
                Best practice
              </strong>
              Finish shift master data and holidays for the year, then build and publish the monthly
              roster before the period starts.
            </div>
          </div>
        </section>

        <h2 className="rpg-section-title">Step-by-step setup</h2>
        <div className="rpg-steps">
          {SETUP_STEPS.map((step, index) => (
            <article key={step.title} className="rpg-step">
              <div className="rpg-step-head">
                <span className="rpg-step-num">{index + 1}</span>
                <h3>{step.title}</h3>
              </div>
              <p>{step.body}</p>
            </article>
          ))}
        </div>

        <div className="rpg-flow">
          <h3 className="text-sm font-bold text-slate-900">Typical approval flow</h3>
          <p className="text-xs text-slate-500 mt-1">
            Status moves forward as HR and admins complete each stage. Published rosters drive
            attendance when roster mode is enabled.
          </p>
          <div className="rpg-flow-track" role="list">
            {LIFECYCLE.map((stage, i) => (
              <span key={stage.label} className="contents" role="listitem">
                <span
                  className={`rpg-flow-pill ${stage.active ? "rpg-flow-pill--active" : ""}`}
                >
                  {stage.label}
                </span>
                {i < LIFECYCLE.length - 1 ? (
                  <span className="rpg-flow-arrow" aria-hidden>
                    →
                  </span>
                ) : null}
              </span>
            ))}
          </div>
        </div>

        <h2 className="rpg-section-title">Related configuration</h2>
        <div className="rpg-links">
          <Link to="/settings/attendance-leave-setup?tab=shifts" className="rpg-link-card">
            <span className="rpg-link-icon rpg-link-icon--shift" aria-hidden>
              <Clock size={20} />
            </span>
            <span>
              <h4>Shift Management</h4>
              <p>Shift master, timings, and policies</p>
            </span>
            <ArrowRight size={16} className="ml-auto text-slate-400 shrink-0" />
          </Link>
          <Link to="/holiday-calendar" className="rpg-link-card">
            <span className="rpg-link-icon rpg-link-icon--holiday" aria-hidden>
              <CalendarDays size={20} />
            </span>
            <span>
              <h4>Holiday Calendar</h4>
              <p>Organisation holidays — separate from roster plans</p>
            </span>
            <ArrowRight size={16} className="ml-auto text-slate-400 shrink-0" />
          </Link>
        </div>

        <div className="rpg-footer-cta">
          <p>Ready to build or edit assignments for a roster period?</p>
          <Link to="/attendance/roster" className="rpg-cta-primary">
            <CalendarRange size={18} />
            Go to roster planner
          </Link>
        </div>
      </div>
    </div>
  );
}
